import { createReducer } from '@reduxjs/toolkit';

import { WishListDto, WishListItemDto, WishListSupplierDto } from '../models/dto/wish-list-dto';
import { WishListListItem } from '../models/wish-list-list-item';
import {
    itemRemovedFromWishlist,
    wishListCopied,
    wishListCreated,
    wishListDeleted,
    wishListDetailsReceived,
    wishListItemQuantityChanged,
    wishListShared,
    wishListUpdated,
    wishListsReceived,
} from './whish-list-actions';

export interface WhishListState {
    wishLists: WishListListItem[];
    details?: WishListDto;
}

const initialState: WhishListState = {
    wishLists: [],
};

export const whishListReducer = createReducer(initialState, builder => {
    builder.addCase(wishListsReceived, (state, action) => ({
        ...state,
        wishLists: action.payload.lists,
    }));
    builder.addCase(wishListDetailsReceived, (state, action) => ({
        ...state,
        details: action.payload.wishList,
    }));
    builder.addCase(wishListCreated, (state, action) => ({
        ...state,
        wishLists: [...state.wishLists, { ...action.payload.wishList, isDefault: false }],
    }));
    builder.addCase(wishListDeleted, (state, action) => ({
        ...state,
        wishLists: state.wishLists.filter(list => list.id !== action.payload.id),
    }));
    builder.addCase(wishListUpdated, (state, action) => {
        if (!state.details) {
            return state;
        }

        return {
            ...state,
            details: {
                ...state.details,
                name: action.payload.name,
            },
            wishLists: state.wishLists.map(list =>
                list.id === action.payload.id ? { ...list, name: action.payload.name } : list
            ),
        };
    });
    builder.addCase(wishListCopied, (state, action) => ({
        ...state,
        wishLists: [...state.wishLists, { ...action.payload.wishList, isDefault: false }],
    }));
    builder.addCase(itemRemovedFromWishlist, (state, action) => {
        if (!state.details) {
            return state;
        }

        let nextSuppliers = modifySupplierByItemId(state.details.suppliers, action.payload.itemId, supplier => ({
            items: supplier.items.filter(item => item.id !== action.payload.itemId),
        }));
        nextSuppliers = nextSuppliers.filter(supplier => supplier.items.length > 0);

        const hasUnavailableItems = nextSuppliers.reduce(
            (result, supplier) => result || supplier.items.some(item => !item.isAvailableForPurchase),
            false
        );

        return {
            ...state,
            details: {
                ...state.details,
                suppliers: nextSuppliers,
                hasUnavailableItems,
            },
        };
    });
    builder.addCase(wishListItemQuantityChanged, (state, action) => {
        if (!state.details) {
            return state;
        }

        const nextSuppliers = modifyItem(state.details.suppliers, action.payload.itemId, item => ({
            quantity: action.payload.quantity,
        }));

        return {
            ...state,
            details: {
                ...state.details,
                suppliers: nextSuppliers,
            },
        };
    });
    builder.addCase(wishListShared, (state, action) => {
        if (!state.details) {
            return state;
        }

        return {
            ...state,
            details: {
                ...state.details,
                ...action.payload,
            },
            wishLists: state.wishLists.map(list =>
                list.id === action.payload.id
                    ? {
                          ...list,
                          isOwner: true,
                          isSharedWithOtherAccountUsers: action.payload.isSharedWithOtherAccountUsers,
                          isSharedWithOwnAccounts: action.payload.isSharedWithOwnAccounts,
                          sharedAccounts: action.payload.sharedAccounts,
                          type: action.payload.type,
                          infoText: action.payload.infoText ?? '',
                      }
                    : list
            ),
        };
    });
});

const modifySupplierByItemId = (
    suppliers: WishListSupplierDto[],
    itemId: number,
    callback: (supplier: WishListSupplierDto) => Partial<WishListSupplierDto>
): WishListSupplierDto[] => {
    const nextSuppliers = [...suppliers];
    const supplierIndex = nextSuppliers.findIndex(supplier => supplier.items.some(item => item.id === itemId));
    nextSuppliers[supplierIndex] = {
        ...nextSuppliers[supplierIndex],
        ...callback(nextSuppliers[supplierIndex]),
    };
    return nextSuppliers;
};

const modifyItem = (
    suppliers: WishListSupplierDto[],
    itemId: number,
    callback: (article: WishListItemDto) => Partial<WishListItemDto>
): WishListSupplierDto[] => {
    return modifySupplierByItemId(suppliers, itemId, supplier => {
        const articleIndex = supplier.items.findIndex(item => item.id === itemId);
        const nextSupplierItems = [...supplier.items];
        nextSupplierItems[articleIndex] = {
            ...nextSupplierItems[articleIndex],
            ...callback(nextSupplierItems[articleIndex]),
        };
        return { ...supplier, items: nextSupplierItems };
    });
};
